
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart as ChartIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

interface ProgressCardProps {
  isLoading: boolean;
  chartData: Array<{
    date: string;
    score: number;
  }>;
}

export const ProgressCard = ({ isLoading, chartData }: ProgressCardProps) => {
  // Process data to show only every nth point if there are many tests
  const processedData = chartData.length > 7 
    ? chartData.filter((_, index) => index % Math.ceil(chartData.length / 7) === 0)
    : chartData;

  // Add the most recent test result if it's not already included
  if (chartData.length > 0 && processedData[processedData.length - 1] !== chartData[chartData.length - 1]) {
    processedData.push(chartData[chartData.length - 1]);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center text-lg">
          <ChartIcon className="mr-2 h-5 w-5 text-accent" />
          Average Score Progress
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-gray-600">Loading progress data...</p>
        ) : processedData.length > 0 ? (
          <div className="h-[200px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={processedData}>
                <XAxis 
                  dataKey="date"
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return date.toLocaleDateString('en-US', { 
                      month: 'short',
                      day: 'numeric'
                    });
                  }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  domain={[0, 100]}
                  tick={{ fontSize: 12 }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  formatter={(value: number) => [`${value}%`, 'Average Score']}
                  labelFormatter={(label) => {
                    const date = new Date(label);
                    return date.toLocaleDateString('en-US', { 
                      month: 'long', 
                      day: 'numeric',
                      year: 'numeric'
                    });
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="score"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={{ fill: "hsl(var(--primary))", r: 4 }}
                  activeDot={{ r: 6, fill: "hsl(var(--primary))" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <p className="text-gray-600">No progress data available</p>
        )}
      </CardContent>
    </Card>
  );
};
